import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Building2, Package, Cpu, Truck, TrendingDown } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { BienFormModal } from './BienFormModal';

type Bien = {
  id: string;
  clave: string;
  descripcion: string;
  tipo: string;
  categoria_nombre?: string;
  marca?: string;
  modelo?: string;
  numero_serie?: string;
  valor_adquisicion: number;
  depreciacion_acumulada: number;
  valor_en_libros: number;
  unidad_nombre?: string;
  responsable_nombre?: string;
  estado: string;
};

type Tipo = 'todos' | 'mueble' | 'inmueble' | 'intangible';

export function BienesPage() {
  const [tipo, setTipo] = useState<Tipo>('todos');
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ['bienes', tipo, q],
    queryFn: () => {
      const p = new URLSearchParams();
      if (tipo !== 'todos') p.set('tipo', tipo);
      if (q) p.set('q', q);
      p.set('limit', '300');
      return api<{ total: number; items: Bien[] }>(`/bienes?${p}`);
    },
  });

  const items = data?.items ?? [];
  const totalValor = items.reduce((a, b) => a + b.valor_en_libros, 0);
  const totalAdq = items.reduce((a, b) => a + b.valor_adquisicion, 0);
  const totalDepre = items.reduce((a, b) => a + b.depreciacion_acumulada, 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Bienes patrimoniales</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Bienes muebles, inmuebles e intangibles</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            Activos fijos con trazabilidad catastral, resguardos firmados, mantenimientos y depreciación mensual automática.
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)}><Plus size={16} /> Alta patrimonial</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Valor en libros" value={fmt.money(totalValor)} accent="brand" />
        <Stat label="Valor de adquisición" value={fmt.money(totalAdq)} accent="neutral" />
        <Stat label="Depreciación acumulada" value={fmt.money(totalDepre)} accent="warn" />
        <Stat label="Bienes totales" value={items.length.toString()} accent="success" />
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <TabBtn active={tipo === 'todos'} onClick={() => setTipo('todos')}>Todos</TabBtn>
        <TabBtn active={tipo === 'mueble'} onClick={() => setTipo('mueble')}>Muebles</TabBtn>
        <TabBtn active={tipo === 'inmueble'} onClick={() => setTipo('inmueble')}>Inmuebles</TabBtn>
        <TabBtn active={tipo === 'intangible'} onClick={() => setTipo('intangible')}>Intangibles</TabBtn>
      </div>

      <Input
        name="q" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por clave, descripción, número de serie…"
        leading={<Search size={16} />}
      />

      <Card>
        <CardBody className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Building2 className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin bienes. Da de alta el primero.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3 w-12"></th>
                    <th className="px-5 py-3">Clave</th>
                    <th className="px-5 py-3">Descripción</th>
                    <th className="px-5 py-3">Categoría</th>
                    <th className="px-5 py-3">Resguardante</th>
                    <th className="px-5 py-3">Unidad</th>
                    <th className="px-5 py-3 text-right">Adquisición</th>
                    <th className="px-5 py-3 text-right">Depreciado</th>
                    <th className="px-5 py-3 text-right">En libros</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((b) => (
                    <tr key={b.id} className="transition-colors hover:bg-muted/50">
                      <td className="px-5 py-3"><TipoIcon tipo={b.tipo} /></td>
                      <td className="num px-5 py-3 font-semibold text-brand-700 dark:text-brand-300">{b.clave}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{b.descripcion}</div>
                        {(b.marca || b.modelo || b.numero_serie) && (
                          <div className="text-[11px] text-ink-mute">
                            {[b.marca, b.modelo, b.numero_serie ? `#${b.numero_serie}` : null].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-ink-mute">{b.categoria_nombre ?? '—'}</td>
                      <td className="px-5 py-3 text-ink-mute">{b.responsable_nombre ?? '—'}</td>
                      <td className="px-5 py-3 text-ink-mute">{b.unidad_nombre ?? '—'}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(b.valor_adquisicion)}</td>
                      <td className="num px-5 py-3 text-right text-ink-mute">{fmt.money(b.depreciacion_acumulada)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(b.valor_en_libros)}</td>
                      <td className="px-5 py-3"><EstadoBien e={b.estado} /></td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/app/bienes/${b.id}`}>
                          <Button variant="ghost" size="sm">Ver</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <BienFormModal open={formOpen} onClose={() => setFormOpen(false)} onCreated={() => { setFormOpen(false); refetch(); }} />
    </div>
  );
}

function TipoIcon({ tipo }: { tipo: string }) {
  const cfg: Record<string, { i: any; color: string }> = {
    inmueble:   { i: Building2, color: 'text-amber-700' },
    intangible: { i: Cpu, color: 'text-sky-700' },
    vehiculo:   { i: Truck, color: 'text-emerald-700' },
    mueble:     { i: Package, color: 'text-brand-600' },
  };
  const c = cfg[tipo] ?? cfg.mueble;
  const Icon = c.i;
  return (
    <div className={cn('flex h-8 w-8 items-center justify-center rounded-md bg-canvas', c.color)}>
      <Icon size={16} />
    </div>
  );
}

function EstadoBien({ e }: { e: string }) {
  const c: Record<string, string> = {
    activo: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    baja: 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
    en_reparacion: 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    prestado: 'bg-sky-50 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', c[e] ?? 'bg-muted text-ink-mute')}>{e.replace('_', ' ')}</span>;
}

function Stat({ label, value, accent }: { label: string; value: string; accent: 'brand' | 'success' | 'warn' | 'neutral' }) {
  const bar = {
    brand: 'before:bg-brand-500',
    success: 'before:bg-emerald-500',
    warn: 'before:bg-amber-500',
    neutral: 'before:bg-ink-soft',
  }[accent];
  return (
    <div className={cn(
      'relative overflow-hidden rounded-lg border border-border bg-surface p-5 shadow-card',
      'before:absolute before:inset-x-0 before:top-0 before:h-[3px]', bar,
    )}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-3 text-[22px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function TabBtn({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative px-4 py-2.5 text-[13px] font-medium transition-colors',
        active ? 'text-brand-600' : 'text-ink-mute hover:text-ink',
      )}
    >
      {children}
      {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-500" />}
    </button>
  );
}

// Placeholder icon for hidden
// @ts-expect-error unused
const _ = TrendingDown;
